/** Type representing RGB color values as a tuple of three numbers */
type RGB = [number, number, number];

/** 3x3 RGB transform matrix used for colorblind simulation */
export interface ColorblindMatrix {
  r: [number, number, number];
  g: [number, number, number];
  b: [number, number, number];
}

export const COLORBLIND_TYPES = [
  "Protanopia",
  "Protanomaly",
  "Deuteranopia",
  "Deuteranomaly",
  "Tritanopia",
  "Tritanomaly",
  "Achromatopsia",
  "Anomalous Trichromacy",
] as const;

/**
 * Converts a hex color string to RGB values
 */
function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
}

/**
 * Converts RGB values to a hex color string
 */
function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Returns the RGB simulation matrix for a colorblind type, if supported.
 */
export function getColorblindMatrix(type: string): ColorblindMatrix | null {
  switch (type.toLowerCase()) {
    case "protanopia":
      return {
        r: [0.567, 0.433, 0],
        g: [0.558, 0.442, 0],
        b: [0.242, 0, 0.758],
      };
    case "protanomaly":
      return {
        r: [0.817, 0.183, 0],
        g: [0.333, 0.667, 0],
        b: [0.125, 0, 0.875],
      };
    case "deuteranopia":
      return {
        r: [0.625, 0.375, 0],
        g: [0.7, 0.3, 0],
        b: [0.3, 0, 0.7],
      };
    case "deuteranomaly":
      return {
        r: [0.8, 0.2, 0],
        g: [0.258, 0.742, 0],
        b: [0.142, 0, 0.858],
      };
    case "tritanopia":
      return {
        r: [0.95, 0, 0.05],
        g: [0.433, 0.567, 0],
        b: [0.475, 0, 0.525],
      };
    case "tritanomaly":
      return {
        r: [0.967, 0, 0.033],
        g: [0.733, 0.267, 0],
        b: [0.183, 0, 0.817],
      };
    case "achromatopsia":
      return {
        r: [0.299, 0.587, 0.114],
        g: [0.299, 0.587, 0.114],
        b: [0.299, 0.587, 0.114],
      };
    case "anomalous trichromacy":
      return {
        r: [0.8, 0.2, 0],
        g: [0.2, 0.8, 0],
        b: [0.2, 0.2, 0.6],
      };
    default:
      return null;
  }
}

/**
 * Applies a colorblind simulation matrix to an RGB pixel.
 */
export function adjustRgbForColorblindness(
  r: number,
  g: number,
  b: number,
  type: string,
): RGB {
  const matrix = getColorblindMatrix(type);
  if (!matrix) {
    return [r, g, b];
  }

  return [
    matrix.r[0] * r + matrix.r[1] * g + matrix.r[2] * b,
    matrix.g[0] * r + matrix.g[1] * g + matrix.g[2] * b,
    matrix.b[0] * r + matrix.b[1] * g + matrix.b[2] * b,
  ];
}

/**
 * Adjusts colors to simulate various types of color blindness.
 */
export function adjustColorForColorblindness(
  color: string,
  type: string,
): string {
  if (!type || type.toLowerCase() === "none") {
    return color;
  }

  const rgb = hexToRgb(color);
  const adjusted = adjustRgbForColorblindness(rgb[0], rgb[1], rgb[2], type);

  if (adjusted === rgb) {
    return color;
  }

  return rgbToHex(
    Math.round(adjusted[0]),
    Math.round(adjusted[1]),
    Math.round(adjusted[2]),
  );
}

/**
 * Builds an SVG feColorMatrix values string from a colorblind matrix.
 */
export function getColorblindFeColorMatrixValues(
  matrix: ColorblindMatrix,
): string {
  const { r, g, b } = matrix;
  return [
    r[0],
    r[1],
    r[2],
    0,
    0,
    g[0],
    g[1],
    g[2],
    0,
    0,
    b[0],
    b[1],
    b[2],
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ].join(" ");
}

/**
 * Normalizes a colorblind type label into a stable SVG filter id suffix.
 */
export function getColorblindFilterId(type: string): string | null {
  const matrix = getColorblindMatrix(type);
  if (!matrix) {
    return null;
  }

  return `colorblind-${type.toLowerCase().replace(/\s+/g, "-")}`;
}

/**
 * Returns CSS filter styles for images when colorblind mode is enabled.
 * Original image data is never modified.
 */
export function getColorblindImageFilterStyle(
  type: string,
  isEnabled: boolean,
): { filter?: string } {
  if (!isEnabled) {
    return {};
  }

  const filterId = getColorblindFilterId(type);
  if (!filterId) {
    return {};
  }

  return { filter: `url(#${filterId})` };
}
